import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import { DEVICE_PIN_LENGTH } from 'entities/device';
import {
	ActivateDeviceMutationParams,
	ActivateDeviceResponse,
	createActivateDeviceMutation,
} from 'shared/api';
import {
	equalLengthRule,
	notEmptyRule,
	notify,
	createModalFormModel,
	ModalFormModel,
} from 'shared/lib';

export type ActivateDeviceFormValue = {
	pin: string;
};

export type ActivateDeviceModel = ModalFormModel<null, ActivateDeviceFormValue> & {
	activateDeviceMutation: Mutation<
		ActivateDeviceMutationParams,
		ActivateDeviceResponse,
		JsonApiRequestError
	>;
	$pending: Store<boolean>;
};

export const createActivateDeviceModel = ($gameId: Store<number | null>): ActivateDeviceModel => {
	const activateDeviceMutation = createActivateDeviceMutation();

	const model = createModalFormModel<null, ActivateDeviceFormValue>({
		fields: {
			pin: {
				init: '',
				rules: [notEmptyRule, equalLengthRule(DEVICE_PIN_LENGTH)],
			},
		},
		validateOn: ['submit'],
		resetErrorsOnChange: true,
	});

	const { $pending } = activateDeviceMutation;

	sample({
		clock: model.form.submitted,
		source: { formValues: model.form.$values, gameId: $gameId },
		fn: ({ formValues, gameId }) => ({
			cashdeskCode: formValues.pin,
			gameId: gameId!,
		}),
		target: activateDeviceMutation.start,
	});

	chain(activateDeviceMutation.finished.success, model.switcher.disable);

	notify(activateDeviceMutation.finished.success, 'activate-device.success');

	chain(model.modalClosed, activateDeviceMutation.reset);

	return {
		activateDeviceMutation,
		$pending,
		...model,
	};
};
